import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { visaCasesAPI } from '../services/api';
import { useOpenParam } from '../utils/openParam';
import {
  FiRefreshCw, FiPlus, FiCopy, FiCheck, FiX, FiSearch, FiMessageCircle, FiAlertTriangle,
  FiClock, FiUpload, FiFileText, FiChevronLeft, FiDownload,
} from 'react-icons/fi';
import './VisaCases.css';

const KINDS = [
  { key: 'STUDENT', label: 'Student' },
  { key: 'JOB', label: 'Job Seeker' },
  { key: 'TOURIST', label: 'Tourist' },
  { key: 'MEDICAL', label: 'Medical Tourism' },
];

const STATUSES = [
  { key: 'OPEN', label: 'Collecting' },
  { key: 'SUBMITTED', label: 'Submitted' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'REFUSED', label: 'Refused' },
  { key: 'CANCELLED', label: 'Cancelled' },
  { key: '', label: 'All' },
];

const PAGE_SIZE = 25;
const NATIONALITIES = ['Russia', 'Kazakhstan', 'Libya', 'Nigeria', 'Zimbabwe'];

const FLAG_LABELS = {
  dual_citizen: 'Dual citizen',
  foreign_resident: 'Foreign national living in the filing country',
  still_student: 'Still enrolled elsewhere',
  family_application: 'Family applying together',
  no_local_bank: 'No local bank account',
  paid_cash: 'Paid in cash',
  paid_exchange: 'Paid through an exchange',
  sponsor_spouse: 'Sponsor is spouse',
  sponsor_employed: 'Sponsor is employed',
  sponsor_owner: 'Sponsor owns a company',
  sponsor_abroad: 'Sponsor is outside the country',
  sponsor_company: 'Sponsor is a company',
  sponsor_person: 'Sponsor is a person',
  parents_divorced: 'Parents divorced',
  parent_deceased: 'A parent is deceased',
  parent_not_travelling: 'A parent is not travelling',
  parent_abroad: 'A parent is outside the country',
  visiting_host: 'Staying with a host in Türkiye',
  invited_by_citizen: 'Invited by a Turkish citizen (no hotel)',
  spouse_applicant: 'Spouse of the Turkish citizen',
  child_applicant: 'Child of the Turkish citizen',
  next_country_visa: 'Next country needs a visa',
  self_employed: 'Self-employed / freelancer',
};

const GROUP_ORDER = ['CORE', 'PURPOSE', 'STATUS', 'TRAVEL'];
const GROUP_LABEL = { CORE: 'Core documents', PURPOSE: 'Purpose documents', STATUS: 'Applicant status', TRAVEL: 'Bookings' };

const ROUTE_TONE = { EXEMPT: 'ok', E_VISA: 'warn', MISSION: 'info', NOT_ELIGIBLE: 'bad', NEEDS_AGE: 'warn', UNKNOWN: 'warn' };

const errText = (err, fallback) => {
  const d = err?.response?.data || {};
  return d.error || d.detail || Object.values(d).flat()[0] || fallback;
};

async function downloadItemFile(item, which) {
  const res = await visaCasesAPI.downloadFile(item.id, which);
  const type = res.headers?.['content-type'] || 'application/octet-stream';
  const ext = type.includes('pdf') ? 'pdf' : type.includes('png') ? 'png' : type.includes('jpeg') ? 'jpg' : 'bin';
  const url = URL.createObjectURL(new Blob([res.data], { type }));
  const link = document.createElement('a');
  link.href = url;
  // The browser cannot read Content-Disposition across origins, so name the file from the item.
  link.download = `${item.key}_${which}.${ext}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// Same name the server sets; the browser cannot read Content-Disposition across origins.
const checklistFileName = (visaType) => {
  const name = visaType.name.split('(')[0].trim().replace(/[^A-Za-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return `Turkiye_${name}_Visa_Checklist.pdf`;
};

async function downloadChecklist(template) {
  const res = await visaCasesAPI.checklistPdf(template.id);
  const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = checklistFileName(template.visa_type);
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

function ChecklistButton({ template, label = 'PDF', className = 'vc-btn vc-btn-sm' }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const go = async () => {
    setBusy(true); setFailed(false);
    try { await downloadChecklist(template); } catch { setFailed(true); } finally { setBusy(false); }
  };
  return (
    <button type="button" className={className} disabled={busy} onClick={go} title="Branded checklist to send to the client">
      <FiDownload /> {busy ? 'Preparing…' : failed ? 'Failed, retry' : label}
    </button>
  );
}

function ChecklistsPanel({ catalog, onClose }) {
  const missions = useMemo(() => {
    const m = {};
    (catalog?.templates || []).forEach((t) => { (m[t.mission.name] = m[t.mission.name] || []).push(t); });
    return Object.entries(m);
  }, [catalog]);
  return (
    <div className="vc-overlay" role="dialog" aria-modal="true">
      <div className="vc-modal">
        <div className="vc-modal-head">
          <h2>Client checklists</h2>
          <button type="button" className="vc-icon-btn" onClick={onClose} aria-label="Close"><FiX /></button>
        </div>
        <p className="vc-muted">Branded PDF checklists to send to clients. Each is built from the current template, so edits made in admin show up in the next download.</p>
        {!catalog && <p className="vc-muted">Loading…</p>}
        {missions.map(([mission, templates]) => (
          <div key={mission} className="vc-checklists">
            <h3>{mission}</h3>
            <ul>
              {templates.map((t) => (
                <li key={t.id}>
                  <div>
                    <strong>{t.visa_type.name}</strong>
                    <span className="vc-muted"> · {t.visa_type.service_line_display} · {t.item_count} items</span>
                  </div>
                  <ChecklistButton template={t} label="Download PDF" />
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

const fmtDate = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '');

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="vc-btn"
      onClick={() => navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1800); })}
    >
      {copied ? <FiCheck /> : <FiCopy />} {copied ? 'Copied' : 'Copy link'}
    </button>
  );
}

function ProgressBar({ progress }) {
  const total = progress.required_total || 0;
  const pct = total ? Math.round((progress.required_received / total) * 100) : 0;
  return (
    <div className="vc-progress" title={`${progress.required_received} of ${total} required documents received`}>
      <div className="vc-progress-bar"><span style={{ width: `${pct}%` }} /></div>
      <span className="vc-progress-num">{progress.required_received}/{total}</span>
    </div>
  );
}

function Badges({ progress }) {
  return (
    <span className="vc-badges">
      {progress.awaiting_check > 0 && <span className="vc-badge vc-badge-info"><FiUpload /> {progress.awaiting_check} to check</span>}
      {progress.stale > 0 && <span className="vc-badge vc-badge-bad"><FiAlertTriangle /> {progress.stale} stale</span>}
      {progress.expiring > 0 && <span className="vc-badge vc-badge-warn"><FiClock /> {progress.expiring} expiring</span>}
      {progress.ready_to_submit && <span className="vc-badge vc-badge-ok"><FiCheck /> ready</span>}
    </span>
  );
}

function RouteBanner({ route }) {
  if (!route) return null;
  return (
    <div className={`vc-route vc-route-${ROUTE_TONE[route.route] || 'info'}`}>
      <strong>{route.nationality_name || 'Passport'}: {route.label}</strong>
      {route.stay_limit && <span> · {route.stay_limit}</span>}
      <ul>{route.notes.map((n) => <li key={n}>{n}</li>)}</ul>
      {route.basis === 'third-party' && <span className="vc-muted">Based on third-party sources: confirm before advising.</span>}
    </div>
  );
}

function RouteCheck({ nationality, visaCode, compact }) {
  const [age, setAge] = useState('');
  const [western, setWestern] = useState(false);
  const [route, setRoute] = useState(null);
  useEffect(() => {
    if (!nationality || !visaCode) { setRoute(null); return; }
    visaCasesAPI.routeCheck({ nationality, visa_type: visaCode, age, holds_western_visa: western })
      .then(({ data }) => setRoute(data)).catch(() => setRoute(null));
  }, [nationality, visaCode, age, western]);
  return (
    <div className="vc-routecheck">
      {!compact && (
        <div className="vc-row">
          <label className="vc-label">Age (Libya rule)
            <input type="number" min="0" max="120" value={age} onChange={(e) => setAge(e.target.value)} placeholder="Optional" />
          </label>
          <label className="vc-check">
            <input type="checkbox" checked={western} onChange={(e) => setWestern(e.target.checked)} />
            Holds a valid Schengen / US / UK / Ireland visa or permit
          </label>
        </div>
      )}
      <RouteBanner route={route} />
    </div>
  );
}

function FlagsEditor({ flags, onChange, known }) {
  return (
    <div className="vc-flags">
      {known.map((f) => (
        <label key={f} className="vc-check">
          <input type="checkbox" checked={!!flags[f]} onChange={(e) => onChange({ ...flags, [f]: e.target.checked })} />
          {FLAG_LABELS[f] || f}
        </label>
      ))}
    </div>
  );
}

function NewCasePanel({ catalog, onClose, onCreated, onOpenExisting }) {
  const [kind, setKind] = useState('STUDENT');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [client, setClient] = useState(null);
  const [templateId, setTemplateId] = useState('');
  const [nationality, setNationality] = useState('');
  const [category, setCategory] = useState('SELF_FUNDED');
  const [flags, setFlags] = useState({});
  const [appointment, setAppointment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [existingId, setExistingId] = useState(null);

  const templates = catalog?.templates || [];
  const template = templates.find((t) => String(t.id) === String(templateId));

  useEffect(() => {
    if (client || query.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(() => {
      visaCasesAPI.searchClients({ client_kind: kind, q: query.trim() }).then(({ data }) => setResults(data || [])).catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [kind, query, client]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setExistingId(null);
    if (!client) { setError('Pick the client first.'); return; }
    if (!templateId) { setError('Pick the visa type.'); return; }
    setBusy(true);
    try {
      const { data } = await visaCasesAPI.create({
        client_kind: kind, client_id: client.id, template: Number(templateId), nationality, applicant_category: category,
        flags, appointment_date: appointment || null,
      });
      onCreated(data);
    } catch (err) {
      setError(errText(err, 'Could not create the case.'));
      if (err?.response?.status === 409) setExistingId(err.response.data?.existing_case_id || null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vc-overlay" role="dialog" aria-modal="true">
      <div className="vc-modal">
        <div className="vc-modal-head">
          <h2>New visa case</h2>
          <button type="button" className="vc-icon-btn" onClick={onClose} aria-label="Close"><FiX /></button>
        </div>
        <form onSubmit={submit} className="vc-form">
          <div className="vc-row">
            <label className="vc-label">Client type
              <select value={kind} onChange={(e) => { setKind(e.target.value); setClient(null); setQuery(''); }}>
                {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
            </label>
            <label className="vc-label">Client
              {client ? (
                <div className="vc-picked">
                  <span><strong>{client.name}</strong> <span className="vc-muted">{client.ref}</span></span>
                  <button type="button" className="vc-link" onClick={() => { setClient(null); setQuery(''); }}>Change</button>
                </div>
              ) : (
                <div className="vc-search"><FiSearch />
                  <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Name, phone, email or ID" autoFocus />
                </div>
              )}
            </label>
          </div>
          {!client && results.length > 0 && (
            <ul className="vc-results">
              {results.map((r) => (
                <li key={r.id}><button type="button" onClick={() => setClient(r)}>
                  <strong>{r.name}</strong> <span className="vc-muted">{r.ref} {r.phone && `· ${r.phone}`}</span>
                </button></li>
              ))}
            </ul>
          )}

          <div className="vc-row">
            <label className="vc-label">Visa type and mission
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                <option value="">Choose…</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.visa_type.name} ({t.mission.name.split('(')[0].trim()}, up to {t.item_count} items)</option>
                ))}
              </select>
            </label>
            <label className="vc-label">Passport (nationality)
              <input list="vc-nationalities" value={nationality} onChange={(e) => setNationality(e.target.value)} placeholder="e.g. Russia" />
              <datalist id="vc-nationalities">{NATIONALITIES.map((n) => <option key={n} value={n} />)}</datalist>
            </label>
          </div>

          {template && nationality && <RouteCheck nationality={nationality} visaCode={template.visa_type.code} />}
          {template && nationality && template.mission.country.toLowerCase() !== nationality.trim().toLowerCase() && (
            <div className="vc-route vc-route-warn">
              <strong>Different country</strong>
              <ul>
                <li>This is the {template.mission.name} checklist, for applicants filing in {template.mission.country}.</li>
                <li>A client from {nationality} files at the Turkish mission in their own country or country of residence. Confirm that mission's list and adjust anything that names {template.mission.country}.</li>
              </ul>
            </div>
          )}
          {template && <p className="vc-muted">{template.visa_type.summary}</p>}

          <div className="vc-row">
            <label className="vc-label">Applicant status
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {(catalog?.applicant_categories || []).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </label>
            <label className="vc-label">Appointment date (optional)
              <input type="date" value={appointment} onChange={(e) => setAppointment(e.target.value)} />
            </label>
          </div>

          <div className="vc-label">Situation (adds only the documents that apply)</div>
          <FlagsEditor flags={flags} onChange={setFlags} known={catalog?.flags || []} />

          {error && <div className="vc-error">{error}</div>}
          {existingId && (
            <button type="button" className="vc-btn" onClick={() => onOpenExisting(existingId)}>Open the existing case</button>
          )}
          <div className="vc-modal-actions">
            <button type="button" className="vc-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="vc-btn vc-btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create case'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ItemRow({ item, onAction, busy }) {
  const [mode, setMode] = useState(null); // 'accept' | 'reject' | 'upload'
  const [docDate, setDocDate] = useState(item.document_date || '');
  const [stamp, setStamp] = useState(item.stamp_confirmed);
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [err, setErr] = useState('');

  const pendingFile = item.has_client_upload || item.has_staff_upload;
  const needsDate = !!item.freshness_days;
  const needsStamp = item.requires_wet_stamp;
  const isCheck = item.kind === 'CHECK';

  const download = async (which) => {
    setErr('');
    try { await downloadItemFile(item, which); } catch (e) { setErr('Could not download that file.'); }
  };

  const run = async (fn) => {
    setErr('');
    try { await fn(); setMode(null); setNote(''); setFile(null); } catch (e) { setErr(errText(e, 'That did not work.')); }
  };

  return (
    <li className={`vc-item vc-item-${item.status.toLowerCase()}`}>
      <div className="vc-item-main">
        <div className="vc-item-title">
          <span className={`vc-dot vc-dot-${item.status.toLowerCase()}`} title={item.status_display} />
          <strong>{item.label}</strong>
          {item.is_optional && <span className="vc-tag">optional</span>}
          {needsStamp && <span className="vc-tag vc-tag-stamp">wet stamp</span>}
          {needsDate && <span className="vc-tag">max {item.freshness_days} days old</span>}
          {item.freshness === 'STALE' && <span className="vc-badge vc-badge-bad"><FiAlertTriangle /> stale by appointment</span>}
          {item.freshness === 'EXPIRING' && <span className="vc-badge vc-badge-warn"><FiClock /> expiring</span>}
        </div>
        {item.guidance && <div className="vc-item-guidance">{item.guidance}</div>}
        {item.staff_note && <div className="vc-item-staffnote">Staff note: {item.staff_note}</div>}
        <div className="vc-item-meta">
          <span>{item.status_display}</span>
          {item.document_date && <span>dated {fmtDate(item.document_date)}{item.due_by && `, valid to ${fmtDate(item.due_by)}`}</span>}
          {item.has_client_upload && <button type="button" className="vc-link" onClick={() => download('client')}><FiFileText /> client file</button>}
          {item.has_staff_upload && <button type="button" className="vc-link" onClick={() => download('staff')}><FiFileText /> staff file</button>}
          {item.rejection_note && <span className="vc-rej">Rejected: {item.rejection_note}</span>}
        </div>
        {err && !mode && <div className="vc-error">{err}</div>}
      </div>

      <div className="vc-item-actions">
        {isCheck && item.status !== 'RECEIVED' && item.status !== 'NOT_NEEDED' && (
          <button type="button" className="vc-btn vc-btn-sm" disabled={busy} onClick={() => run(() => onAction('confirm', item))}>Confirm</button>
        )}
        {!isCheck && item.status === 'UPLOADED_AWAITING_CHECK' && pendingFile && (
          <>
            <button type="button" className="vc-btn vc-btn-sm vc-btn-primary" onClick={() => setMode('accept')}>Accept</button>
            <button type="button" className="vc-btn vc-btn-sm" onClick={() => setMode('reject')}>Reject</button>
          </>
        )}
        {!isCheck && item.status !== 'NOT_NEEDED' && item.status !== 'RECEIVED' && (
          <button type="button" className="vc-btn vc-btn-sm" onClick={() => setMode('upload')}>Attach</button>
        )}
        {item.status === 'NOT_NEEDED' ? (
          <button type="button" className="vc-btn vc-btn-sm" disabled={busy} onClick={() => run(() => onAction('reopen', item))}>Needed after all</button>
        ) : (
          item.status !== 'RECEIVED' && (
            <button type="button" className="vc-btn vc-btn-sm" disabled={busy} onClick={() => run(() => onAction('notNeeded', item))}>Not needed</button>
          )
        )}
        {item.status === 'RECEIVED' && !isCheck && (
          <button type="button" className="vc-btn vc-btn-sm" onClick={() => setMode('accept')}>Edit date / stamp</button>
        )}
      </div>

      {mode && (
        <div className="vc-item-form">
          {(mode === 'accept' || mode === 'upload') && (needsDate || needsStamp) && (
            <div className="vc-row">
              {needsDate && (
                <label className="vc-label">Date on the document
                  <input type="date" value={docDate} onChange={(e) => setDocDate(e.target.value)} />
                </label>
              )}
              {needsStamp && (
                <label className="vc-check">
                  <input type="checkbox" checked={stamp} onChange={(e) => setStamp(e.target.checked)} />
                  I checked the original wet stamp
                </label>
              )}
            </div>
          )}
          {mode === 'upload' && (
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files[0])} />
          )}
          {mode === 'reject' && (
            <input className="vc-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why, so the client can fix it" />
          )}
          {err && <div className="vc-error">{err}</div>}
          <div className="vc-item-form-actions">
            <button type="button" className="vc-btn vc-btn-sm" onClick={() => { setMode(null); setErr(''); }}>Cancel</button>
            <button
              type="button" className="vc-btn vc-btn-sm vc-btn-primary" disabled={busy}
              onClick={() => {
                if (mode === 'upload' && !file) { setErr('Choose a file first.'); return; }
                run(() => {
                if (mode === 'accept') return onAction('accept', item, { document_date: docDate || undefined, stamp_confirmed: stamp, upload_version: item.upload_version });
                if (mode === 'reject') return onAction('reject', item, note, item.upload_version);
                return onAction('upload', item, file, { document_date: docDate, stamp_confirmed: stamp ? 'true' : '' });
                });
              }}
            >
              {mode === 'accept' ? 'Accept' : mode === 'reject' ? 'Reject' : 'Attach and save'}
            </button>
          </div>
        </div>
      )}
    </li>
  );
}

function CaseDetail({ caseId, catalog, onBack, onChanged }) {
  const [kase, setKase] = useState(null);
  const [items, setItems] = useState([]);
  const [link, setLink] = useState(null);
  const [edit, setEdit] = useState(false);
  const [flags, setFlags] = useState({});
  const [category, setCategory] = useState('');
  const [appointment, setAppointment] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const [c, i] = await Promise.all([visaCasesAPI.get(caseId), visaCasesAPI.items(caseId)]);
    setKase(c.data); setItems(i.data);
    setFlags(c.data.flags || {}); setCategory(c.data.applicant_category);
    setAppointment(c.data.appointment_date || ''); setStatus(c.data.status);
  }, [caseId]);

  useEffect(() => { load().catch(() => setError('Could not load this case.')); }, [load]);

  const showLink = async (fn) => {
    setError('');
    try { const { data } = await fn(); setLink(data); } catch (e) { setError(errText(e, 'Could not get the link.')); }
  };

  const onAction = async (kind, item, a, b) => {
    setBusy(true);
    try {
      if (kind === 'accept') await visaCasesAPI.accept(item.id, a);
      else if (kind === 'reject') await visaCasesAPI.reject(item.id, a, b);
      else if (kind === 'notNeeded') await visaCasesAPI.notNeeded(item.id);
      else if (kind === 'reopen') await visaCasesAPI.reopen(item.id);
      else if (kind === 'confirm') await visaCasesAPI.confirm(item.id);
      else if (kind === 'upload') await visaCasesAPI.upload(item.id, a, b);
      await load(); onChanged();
    } catch (e) {
      // 409: the client uploaded a newer file while staff were reviewing. Show the new state.
      if (e?.response?.status === 409) await load();
      throw e;
    } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    setBusy(true); setError('');
    try {
      await visaCasesAPI.update(caseId, { flags, applicant_category: category, appointment_date: appointment || null, status });
      setEdit(false); await load(); onChanged();
    } catch (e) { setError(errText(e, 'Could not save.')); } finally { setBusy(false); }
  };

  const grouped = useMemo(() => {
    const g = {};
    items.forEach((i) => { (g[i.group] = g[i.group] || []).push(i); });
    return g;
  }, [items]);

  if (!kase) return <div className="vc-detail">{error ? <div className="vc-error">{error}</div> : <p className="vc-muted">Loading…</p>}</div>;

  return (
    <div className="vc-detail">
      <button type="button" className="vc-link vc-back" onClick={onBack}><FiChevronLeft /> All cases</button>
      <div className="vc-detail-head">
        <div>
          <h2>{kase.client_name} <span className="vc-muted">{kase.client_ref}</span></h2>
          <div className="vc-muted">
            {kase.visa_type.name} · {kase.mission.name} · {kase.client_kind_display}
            {kase.nationality && ` · ${kase.nationality} passport`}
            {kase.appointment_date && ` · appointment ${fmtDate(kase.appointment_date)}`}
          </div>
        </div>
        <div className="vc-detail-side">
          <ChecklistButton template={{ id: kase.template, visa_type: kase.visa_type }} label="Checklist PDF" />
          <ProgressBar progress={kase.progress} />
          <Badges progress={kase.progress} />
          <span className={`vc-status vc-status-${kase.status.toLowerCase()}`}>{kase.status_display}</span>
        </div>
      </div>

      {error && <div className="vc-error">{error}</div>}

      {kase.nationality && <RouteCheck nationality={kase.nationality} visaCode={kase.visa_type.code} />}

      <div className="vc-panel">
        <div className="vc-panel-head">
          <strong>Client link</strong>
          <span className="vc-muted">The client uploads their own documents, no login.</span>
        </div>
        {link ? (
          <div className="vc-share">
            <div className="vc-share-url">{link.url}</div>
            <div className="vc-share-actions">
              <CopyButton text={link.url} />
              {link.whatsapp_url && <a className="vc-btn vc-btn-wa" href={link.whatsapp_url} target="_blank" rel="noreferrer"><FiMessageCircle /> WhatsApp</a>}
              <button type="button" className="vc-btn" onClick={() => showLink(() => visaCasesAPI.regenerateLink(caseId))}>New link</button>
              <button type="button" className="vc-btn" onClick={() => showLink(() => visaCasesAPI.revokeLink(caseId))}>Turn off</button>
            </div>
            {!link.is_valid && <p className="vc-error">This link is turned off.</p>}
            {!link.whatsapp_url && link.is_valid && <p className="vc-muted">No phone number on file: copy the link and send it yourself.</p>}
          </div>
        ) : (
          <button type="button" className="vc-btn" onClick={() => showLink(() => visaCasesAPI.link(caseId))}>Show client link</button>
        )}
      </div>

      <div className="vc-panel">
        <div className="vc-panel-head">
          <strong>Situation</strong>
          {!edit && <button type="button" className="vc-link" onClick={() => setEdit(true)}>Edit</button>}
        </div>
        {edit ? (
          <>
            <div className="vc-row">
              <label className="vc-label">Applicant status
                <select value={category} onChange={(e) => setCategory(e.target.value)}>
                  {(catalog?.applicant_categories || []).map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
              <label className="vc-label">Appointment date
                <input type="date" value={appointment} onChange={(e) => setAppointment(e.target.value)} />
              </label>
              <label className="vc-label">Case status
                <select value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUSES.filter((s) => s.key).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
              </label>
            </div>
            <FlagsEditor flags={flags} onChange={setFlags} known={catalog?.flags || []} />
            <p className="vc-muted">Changing this adds newly needed documents and marks untouched, no-longer-needed ones as not needed. Anything already uploaded is kept.</p>
            <div className="vc-item-form-actions">
              <button type="button" className="vc-btn vc-btn-sm" onClick={() => setEdit(false)}>Cancel</button>
              <button type="button" className="vc-btn vc-btn-sm vc-btn-primary" disabled={busy} onClick={saveEdit}>Save</button>
            </div>
          </>
        ) : (
          <div className="vc-muted">
            {kase.category_display}
            {Object.keys(kase.flags || {}).filter((f) => kase.flags[f]).map((f) => ` · ${FLAG_LABELS[f] || f}`)}
          </div>
        )}
      </div>

      {GROUP_ORDER.filter((g) => grouped[g]).map((g) => (
        <div key={g} className="vc-panel">
          <div className="vc-panel-head"><strong>{GROUP_LABEL[g]}</strong></div>
          <ul className="vc-items">
            {grouped[g].map((it) => <ItemRow key={it.id} item={it} onAction={onAction} busy={busy} />)}
          </ul>
        </div>
      ))}
    </div>
  );
}

export default function VisaCases() {
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [dash, setDash] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('OPEN');
  const [visaType, setVisaType] = useState('');
  const [attention, setAttention] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showChecklists, setShowChecklists] = useState(false);
  const [openId, setOpenId] = useState(null);
  useOpenParam('case', (id) => setOpenId(Number(id)));

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [list, d] = await Promise.all([
        visaCasesAPI.list({ client_kind: kind || undefined, status: status || undefined, visa_type: visaType || undefined, search: search || undefined, attention: attention || undefined, page, page_size: PAGE_SIZE }),
        visaCasesAPI.dashboard(),
      ]);
      setCases(list.data.results); setTotal(list.data.count); setDash(d.data);
    } catch (e) { setError(errText(e, 'Could not load visa cases.')); } finally { setLoading(false); }
  }, [kind, status, visaType, search, attention, page]);

  // A different filter means a different result set: go back to its first page.
  useEffect(() => { setPage(1); }, [kind, status, visaType, search, attention]);

  useEffect(() => { visaCasesAPI.catalog().then(({ data }) => setCatalog(data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, search ? 250 : 0); return () => clearTimeout(t); }, [load, search]);

  if (openId) {
    return (
      <div className="vc">
        <CaseDetail caseId={openId} catalog={catalog} onBack={() => setOpenId(null)} onChanged={load} />
      </div>
    );
  }

  return (
    <div className="vc">
      <div className="vc-header">
        <div>
          <h1>Visa Cases</h1>
          <p className="vc-muted">Document tracking for all 12 Turkish visa types, for students, job seekers, tourists and medical clients.</p>
        </div>
        <div className="vc-header-actions">
          <button type="button" className="vc-btn" onClick={load}><FiRefreshCw className={loading ? 'spin' : ''} /> Refresh</button>
          <button type="button" className="vc-btn" onClick={() => setShowChecklists(true)}><FiDownload /> Checklists</button>
          <button type="button" className="vc-btn vc-btn-primary" onClick={() => setShowNew(true)}><FiPlus /> New case</button>
        </div>
      </div>

      {dash && (
        <div className="vc-stats">
          <div className="vc-stat"><div className="vc-stat-value">{dash.open_cases}</div><div className="vc-stat-label">Open cases</div></div>
          <div className="vc-stat"><div className="vc-stat-value">{dash.awaiting_check}</div><div className="vc-stat-label">Documents to check</div></div>
          <div className="vc-stat"><div className={`vc-stat-value ${dash.stale_documents ? 'vc-bad' : ''}`}>{dash.stale_documents}</div><div className="vc-stat-label">Stale documents</div></div>
          <div className="vc-stat"><div className={`vc-stat-value ${dash.appointments_at_risk_count ? 'vc-bad' : ''}`}>{dash.appointments_at_risk_count}</div><div className="vc-stat-label">Appointments at risk (14 days)</div></div>
        </div>
      )}

      {dash && dash.appointments_at_risk.length > 0 && (
        <div className="vc-panel vc-risk">
          <div className="vc-panel-head"><strong><FiAlertTriangle /> Appointments in the next 14 days that are not ready</strong></div>
          <ul className="vc-risk-list">
            {dash.appointments_at_risk.map((c) => (
              <li key={c.id}>
                <button type="button" className="vc-link" onClick={() => setOpenId(c.id)}>{c.client_name}</button>
                <span className="vc-muted"> · {c.visa_type.name} · {fmtDate(c.appointment_date)}</span>
                <ProgressBar progress={c.progress} /><Badges progress={c.progress} />
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="vc-filters">
        <div className="vc-tabs">
          {STATUSES.map((s) => <button key={s.key || 'all'} type="button" className={status === s.key ? 'active' : ''} onClick={() => setStatus(s.key)}>{s.label}</button>)}
        </div>
        <select value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All client types</option>
          {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
        <select value={visaType} onChange={(e) => setVisaType(e.target.value)}>
          <option value="">All visa types</option>
          {(catalog?.visa_types || []).map((v) => <option key={v.code} value={v.code}>{v.name}</option>)}
        </select>
        <label className="vc-check"><input type="checkbox" checked={attention} onChange={(e) => setAttention(e.target.checked)} /> Needs attention</label>
        <div className="vc-search vc-search-inline"><FiSearch />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, ID, passport" />
        </div>
      </div>

      {error && <div className="vc-error">{error}</div>}
      {!loading && cases.length === 0 && <div className="vc-empty">No visa cases match. Use New case to start one.</div>}

      <div className="vc-table">
        {cases.map((c) => (
          <button key={c.id} type="button" className="vc-rowcard" onClick={() => setOpenId(c.id)}>
            <div className="vc-rowcard-main">
              <strong>{c.client_name}</strong> <span className="vc-muted">{c.client_ref} · {c.client_kind_display}</span>
              <div className="vc-muted">
                {c.visa_type.name} · {c.mission.name.split('(')[0].trim()}
                {c.nationality && ` · ${c.nationality}`}
                {c.appointment_date && ` · appt ${fmtDate(c.appointment_date)}`}
              </div>
            </div>
            <div className="vc-rowcard-side">
              <ProgressBar progress={c.progress} />
              <Badges progress={c.progress} />
              <span className={`vc-status vc-status-${c.status.toLowerCase()}`}>{c.status_display}</span>
            </div>
          </button>
        ))}
      </div>

      {total > PAGE_SIZE && (
        <div className="vc-pager">
          <span className="vc-muted">
            Showing {(page - 1) * PAGE_SIZE + 1} to {Math.min(page * PAGE_SIZE, total)} of {total}
          </span>
          <button type="button" className="vc-btn vc-btn-sm" disabled={page <= 1 || loading} onClick={() => setPage((n) => n - 1)}>Previous</button>
          <button type="button" className="vc-btn vc-btn-sm" disabled={page * PAGE_SIZE >= total || loading} onClick={() => setPage((n) => n + 1)}>Next</button>
        </div>
      )}

      {showChecklists && <ChecklistsPanel catalog={catalog} onClose={() => setShowChecklists(false)} />}

      {showNew && (
        <NewCasePanel
          catalog={catalog}
          onOpenExisting={(id) => { setShowNew(false); setOpenId(id); }}
          onClose={() => setShowNew(false)}
          onCreated={(c) => { setShowNew(false); load(); setOpenId(c.id); }}
        />
      )}
    </div>
  );
}
